import { useEvent } from "client-util/useEvent";
import { Spinner } from "components/Loading";
import { PuzzleImage } from "components/PuzzleContent/puzzle-components";
import Globals from "Globals";
import { useState } from "react";
import { BLUE_THEATRE_CONFIG } from "sockapi/scope-specs/team-blue-theatre-spec";
import { ScopeType } from "sockapi/scope-types";
import { Scope } from "sockapi/scopes";
import { useScopeStateResolvable } from "stores/ScopeStateStore";
import { useSockClient, useTeamId } from "stores/ServerInteractionStore";
import "./BlueTheatreShell.css";

export function BlueTheatreShell(props: { staticPuzDir: string }) {
  // local state - adds glow effect before first click
  const [neverClickedDayButton, setNeverClickedDayButton] = useState(true);

  const { staticPuzDir } = props;

  const teamId = useTeamId();
  const teamBlueTheatreState = useScopeStateResolvable(
    ScopeType.TEAM_BLUE_THEATRE,
    teamId === null ? null : { teamId }
  );
  const sockClient = useSockClient();

  const handleNextDay = useEvent(() => {
    if (teamBlueTheatreState === null || sockClient === null || teamId === null)
      return;
    const scope: Scope<ScopeType.TEAM_BLUE_THEATRE> = {
      type: ScopeType.TEAM_BLUE_THEATRE,
      teamId,
    };
    const day = teamBlueTheatreState.day;
    (async () => {
      await sockClient.sendScopeStepAndWaitAsync(scope, {
        day: Math.min(day + 1, BLUE_THEATRE_CONFIG.maxDay),
      });
      setNeverClickedDayButton(false);
    })().catch(console.error);
  });

  const handlePreviousDay = useEvent(() => {
    if (teamBlueTheatreState === null || sockClient === null || teamId === null)
      return;
    const scope: Scope<ScopeType.TEAM_BLUE_THEATRE> = {
      type: ScopeType.TEAM_BLUE_THEATRE,
      teamId,
    };
    const day = teamBlueTheatreState.day;
    (async () => {
      await sockClient.sendScopeStepAndWaitAsync(scope, {
        day: Math.max(day - 1, BLUE_THEATRE_CONFIG.minDay),
      });
      setNeverClickedDayButton(false);
    })().catch(console.error);
  });

  if (teamBlueTheatreState === null || sockClient === null || teamId === null)
    return <Spinner inline={true} />;

  // which shell day image to show
  const { day } = teamBlueTheatreState;

  return (
    <div className="blue-theatre-shell">
      <PuzzleImage
        staticPuzDir={staticPuzDir}
        nameWithExt="shell-shared.jpg"
        fullPiece
        style={{ position: "absolute" }}
      />
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((dayNum) => (
        <PuzzleImage
          key={`bot-day-${dayNum}`}
          staticPuzDir={staticPuzDir}
          name={`shell-bot-day-${dayNum}`}
          fullPiece
          style={{
            zIndex: 1,
            position: "absolute",
            pointerEvents: "none",
            userSelect: "none",
            opacity: day === dayNum ? 1 : 0,
          }}
        />
      ))}
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((dayNum) => (
        <PuzzleImage
          key={`top-day-${dayNum}`}
          staticPuzDir={staticPuzDir}
          name={`shell-top-day-${dayNum}`}
          fullPiece
          style={{
            zIndex: Globals.zIndices.PUZZLE_LAYERS_ABOVE_PIECES_START,
            position: "absolute",
            pointerEvents: "none",
            userSelect: "none",
            opacity: day === dayNum ? 1 : 0,
          }}
        />
      ))}
      <button
        className={`blue-theatre-day-btn prev ${neverClickedDayButton ? "glow" : ""}`}
        onClick={handlePreviousDay}
        style={{
          visibility: day > BLUE_THEATRE_CONFIG.minDay ? "visible" : "hidden",
          position: "absolute",
          top: "calc(765 / 5400 * 100%)",
          right: "calc(1748 / 8400 * 100%)",
          width: "calc(290 / 8400 * 100%)",
          height: "calc(268 / 5400 * 100%)",
          zIndex: Globals.zIndices.PUZZLE_LAYERS_ABOVE_PIECES_START + 1,
        }}
      />
      <button
        className={`blue-theatre-day-btn next ${neverClickedDayButton ? "glow" : ""}`}
        onClick={handleNextDay}
        style={{
          visibility: day < BLUE_THEATRE_CONFIG.maxDay ? "visible" : "hidden",
          position: "absolute",
          top: "calc(765 / 5400 * 100%)",
          right: "calc(455 / 8400 * 100%)",
          width: "calc(290 / 8400 * 100%)",
          height: "calc(268 / 5400 * 100%)",
          zIndex: Globals.zIndices.PUZZLE_LAYERS_ABOVE_PIECES_START + 1,
        }}
      />
    </div>
  );
}
